// 004 (FR-033, FR-034, FR-036; data-model §6.3): the address form's draft and its request bodies.
// Coordinates are rounded to 6 decimals and always sent together; Other carries customLabel;
// edits send only changed properties and clear the landmark with null.
import type { Api } from "@/api/types";
import { round6 } from "@/maps/bridgeProtocol";
import type { LatLng } from "@/maps/types";

import type { AddressDraft } from "../validation";

export type AddressFields = Omit<AddressDraft, "pinPlaced">;

export const emptyDraft = (): AddressFields => ({
  label: "HOME",
  customLabel: "",
  flatHouse: "",
  streetArea: "",
  landmark: "",
  city: "",
  pincode: "",
});

export function draftFromAddress(a: Api["Address"]): AddressFields {
  return {
    label: a.label,
    customLabel: a.customLabel ?? "",
    flatHouse: a.flatHouse,
    streetArea: a.streetArea,
    landmark: a.landmark ?? "",
    city: a.city,
    pincode: a.pincode,
  };
}

export function createAddressBody(
  d: AddressFields,
  pin: LatLng,
): Api["AddressCreate"] {
  const landmark = d.landmark.trim();
  return {
    label: d.label,
    ...(d.label === "OTHER" ? { customLabel: d.customLabel.trim() } : {}),
    flatHouse: d.flatHouse.trim(),
    streetArea: d.streetArea.trim(),
    ...(landmark ? { landmark } : {}),
    city: d.city.trim(),
    pincode: d.pincode.trim(),
    latitude: round6(pin.latitude),
    longitude: round6(pin.longitude),
  };
}

export function updateAddressBody(
  d: AddressFields,
  pin: LatLng,
  original: Api["Address"],
): Api["AddressUpdate"] {
  const body: Api["AddressUpdate"] = {};
  if (d.label !== original.label) body.label = d.label;
  if (
    d.label === "OTHER" &&
    d.customLabel.trim() !== (original.customLabel ?? "")
  )
    body.customLabel = d.customLabel.trim();
  for (const k of ["flatHouse", "streetArea", "city", "pincode"] as const)
    if (d[k].trim() !== original[k]) body[k] = d[k].trim();
  const landmark = d.landmark.trim();
  if (landmark !== (original.landmark ?? ""))
    body.landmark = landmark === "" ? null : landmark;
  const lat = round6(pin.latitude);
  const lng = round6(pin.longitude);
  if (lat !== original.latitude || lng !== original.longitude) {
    body.latitude = lat;
    body.longitude = lng;
  }
  return body;
}
